/**
 * `gamification-core` — proyección de las anotaciones locales a GameEvents.
 *
 * SEÑAL DE LECTURA (racha): bible-strong no tiene un feed de `activity` ni un
 * registro explícito de "capítulo leído" que sincronice con timestamp. Por eso
 * la racha se DERIVA como PROXY de la actividad de anotación que YA existe
 * localmente: la UNIÓN de notas, subrayados (a nivel de versículo y de grupo) y
 * marcadores. Cada día local con al menos una de esas anotaciones cuenta como un
 * día de lectura activo (el motor colapsa los eventos del mismo día por línea de
 * racha). Es NO invasivo: no añade tablas, no toca el servidor ni las sync
 * rules. Puro y determinista: los mismos registros → el mismo `PlayerState`.
 */
import { BSO_EVENTS, bsoGamificationConfig } from "./config";
import { applyEvents, initialState } from "./engine";
import { dayKeyUTC } from "./streaks";
import type { GameEvent, PlayerState } from "./types";

/** Clase de anotación de lectura. */
export type ReadingKind = "note" | "highlight" | "bookmark" | "planDay";

/**
 * Registro mínimo de anotación (proyección de las tablas locales). `createdAt`
 * es el ISO `created_at` de la fila; `null`/inválido se descarta.
 */
export interface ReadingRecord {
  kind: ReadingKind;
  createdAt: string | null | undefined;
}

const EVENT_BY_KIND: Record<ReadingKind, string> = {
  note: BSO_EVENTS.noteCreated,
  highlight: BSO_EVENTS.highlightCreated,
  bookmark: BSO_EVENTS.bookmarkCreated,
  planDay: BSO_EVENTS.planDayCompleted,
};

/** Epoch ms del `created_at` ISO; `null` si falta o no parsea. */
function timestampOf(record: Pick<ReadingRecord, "createdAt">): number | null {
  if (!record.createdAt) {
    return null;
  }
  const t = Date.parse(record.createdAt);
  return Number.isNaN(t) ? null : t;
}

/**
 * Traduce las anotaciones locales a eventos de gamificación en orden cronológico
 * ascendente (como exige el reducer de rachas). Filas sin fecha se ignoran.
 */
export function deriveGameEvents(records: readonly ReadingRecord[]): GameEvent[] {
  const events: GameEvent[] = [];
  for (const record of records) {
    const at = timestampOf(record);
    if (at === null) {
      continue;
    }
    events.push({ type: EVENT_BY_KIND[record.kind], at, day: dayKeyUTC(at) });
  }
  return events.toSorted((a, b) => a.at - b.at);
}

/**
 * Estado de juego derivado de las anotaciones locales. Puro: mismos registros →
 * mismo estado. Retorna XP/nivel/racha de lectura/badges/quests para la UI.
 */
export function deriveGameState(records: readonly ReadingRecord[]): PlayerState {
  const events = deriveGameEvents(records);
  return applyEvents(initialState(), events, bsoGamificationConfig).state;
}
